import { useCallback, useEffect, useReducer, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input } from '@pijma/crypto'
import { useBaseMask, usePhoneMask } from '@pijma/input'

import { sellApplicationClient } from '@api'
import { Dispatch, RootState } from '@stores'
import { TPayFormState } from '@stores/types/*'
import {
  charIsRusLetterWithSpaces,
  nameSurnameRuRegexp,
  phoneRegexp,
  replaceNotRusSymbols,
} from '@utils'
import styled from 'styled-components'

import { TBank } from '../../../../../api/types/TBank'
import { banksService } from '../../../../../service/payform'
import {
  SESSION_PHONE_KEY,
  SESSION_RECEIVER_BANK_KEY,
  SESSION_RECEIVER_KEY,
} from '../../../../../stores/payform/payForm'
import { TBanksState } from '../../../../../stores/types/TBanksState'
import InputValidation from '../../../../../validation/InputValidation'
import { H4 } from '../../../../common/header/Headings'
import { ModalSelectInput } from '../../../../common/input/modalSelect/ModalSelectInput'
import { Island } from '../../../../common/island'
import NavigationBar from '../../../../common/navigation/NavigationBar'
import { BodyText500 } from '../../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 24px;
`

const ModalSelectInputContainer = styled.div`
  width: 100%;
  padding: 0 20px 24px;
  position: relative;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 20px 20px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface ReceiverBanksState {
  fetched: boolean
}

const receiverBanksReducer = (state: ReceiverBanksState) => {
  if (!state.fetched) {
    sellApplicationClient.getNspkBanks().then((res) => banksService.init(res))
    return { fetched: true }
  }
  return state
}

interface StateProps {
  payForm: TPayFormState
  receiverBanks: TBanksState
}

interface DispatchProps {
  setPhone: (phone: string) => void
  setReceiver: (receiver: string) => void
  setReceiverBank: (bank: TBank) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const SellSourceSbpPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()
  const initialReceiverBanks = { fetched: false }

  const getChosenBank = (bankId: string) => {
    return props.receiverBanks.banks.find((bank) => bank.id === bankId) as TBank
  }

  // validation
  //// state
  const [phoneValidation, setPhoneValidation] = useState<InputValidation>({
    valid: true,
  })
  const [receiverValidation, setReceiverValidation] = useState<InputValidation>(
    {
      valid: true,
    },
  )
  const [formValid, setFormValid] = useState<boolean>(false)
  const [, receiverBanksDispatch] = useReducer(
    receiverBanksReducer,
    initialReceiverBanks,
  )

  //// effects
  const validatePhone = useCallback(
    (onlyValid = false) => {
      if (props.payForm.phone && phoneRegexp.test(props.payForm.phone)) {
        setPhoneValidation({ valid: true })
        return true
      }
      if (onlyValid) return false
      setPhoneValidation({
        valid: false,
        message: intl.formatMessage({ id: 'sellSourceSbpPage.10Digits' }),
      })
      return false
    },
    [props.payForm.phone],
  )
  const validateSender = useCallback(
    (onlyValid = false) => {
      if (
        props.payForm.receiver &&
        nameSurnameRuRegexp.test(props.payForm.receiver)
      ) {
        setReceiverValidation({ valid: true })
        return true
      }
      if (onlyValid) return false
      if (props.payForm.receiver == undefined) {
        setReceiverValidation({
          valid: false,
          message: intl.formatMessage({
            id: 'sellSourceSbpPage.nameAndSurnameValidation',
          }),
        })
        return false
      }
      setReceiverValidation({
        valid: false,
        message: intl.formatMessage({
          id: 'sellSourceSbpPage.invalidNameAndSurnameValidation',
        }),
      })
      return false
    },
    [props.payForm.receiver],
  )

  useEffect(() => {
    if (props.payForm.phone) {
      validatePhone(true)
    } else {
      setFormValid(false)
    }
  }, [props.payForm.phone, validatePhone])

  useEffect(() => {
    if (props.payForm.receiver) {
      validateSender(true)
    } else {
      setFormValid(false)
    }
  }, [props.payForm.receiver, validateSender])

  useEffect(() => {
    setFormValid(
      props.payForm.receiver !== undefined &&
        props.payForm.phone !== undefined &&
        phoneValidation.valid &&
        receiverValidation.valid &&
        props.payForm.receiverBank !== undefined,
    )
  }, [
    phoneValidation,
    receiverValidation,
    props.payForm.receiver,
    props.payForm.phone,
    props.payForm.receiverBank,
  ])

  useEffect(() => {
    receiverBanksDispatch()
  }, [])

  // Pijma input masks
  const phoneMask = usePhoneMask({
    mask: {
      onBlur: () => {
        validatePhone()
      },
      value: props.payForm.phone,
      onValueChange: ({ formattedValue }) => {
        props.setPhone(formattedValue)
      },
    },
    country: 'RU',
  })
  const receiverMask = useBaseMask({
    type: 'text',
    format: replaceNotRusSymbols,
    isValidInputCharacter: charIsRusLetterWithSpaces,
    removeFormatting: replaceNotRusSymbols,
    value: props.payForm.receiver,
    onBlur: () => {
      validateSender()
    },
    onValueChange: ({ formattedValue }) => {
      props.setReceiver(formattedValue)
    },
  })

  const goToSumPage = () => {
    validatePhone()
    validateSender()
    sessionStorage.setItem(SESSION_PHONE_KEY, props.payForm.phone + '')
    sessionStorage.setItem(SESSION_RECEIVER_KEY, props.payForm.receiver + '')
    sessionStorage.setItem(
      SESSION_RECEIVER_BANK_KEY,
      JSON.stringify(props.payForm.receiverBank),
    )
    redirectTo(`../../sum`)
  }

  const onBankSelect = (bankId: string) => {
    props.setReceiverBank(getChosenBank(bankId))
  }

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          paymentMethodRedirectLink={'../../source-select'}
          paymentDisabled
          sumDisabled
          noPaymentMethod
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle>
          <H4>{intl.formatMessage({ id: 'sellSourceSbpPage.title' })}</H4>
        </SourceTitle>
        <InputContainer>
          <Input
            {...phoneMask}
            label={intl.formatMessage({ id: 'sellSourceSbpPage.phoneLabel' })}
            width={'100%'}
            disabled={props.payForm.paymentBlocking}
            type={'text'}
            inputMode={'decimal'}
            placeholder={'+7 (000) 000-00-00'}
            errorMessage={phoneValidation.message}
            invalid={!phoneValidation.valid}
            allowClear={true}
            description={intl.formatMessage({
              id: 'sellSourceSbpPage.phoneDescription',
            })}
          />
        </InputContainer>
        <InputContainer>
          <Input
            {...receiverMask}
            label={intl.formatMessage({
              id: 'sellSourceSbpPage.receiverLabel',
            })}
            width={'100%'}
            disabled={props.payForm.paymentBlocking}
            type={'text'}
            description={intl.formatMessage({
              id: 'sellSourceSbpPage.receiverDescription',
            })}
            placeholder={intl.formatMessage({
              id: 'sellSourceSbpPage.receiverPlaceholder',
            })}
            errorMessage={receiverValidation.message}
            invalid={!receiverValidation.valid}
            allowClear={true}
          />
        </InputContainer>
        <ModalSelectInputContainer>
          {props.receiverBanks && (
            <ModalSelectInput
              defaultIcon={'/img/assets/otherBank.svg'}
              data={props.receiverBanks.banks}
              defaultText={intl.formatMessage({
                id: 'sellSourceSbpPage.receiverBankDefaultText',
              })}
              searchTitle={intl.formatMessage({
                id: 'sellSourceSbpPage.receiverBankSearchTitle',
              })}
              searchPlaceholder={intl.formatMessage({
                id: 'sellSourceSbpPage.receiverBankSearchPlaceholder',
              })}
              onSelect={onBankSelect}
              selected={props.payForm.receiverBank?.id}
            />
          )}
        </ModalSelectInputContainer>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            disabled={!formValid}
            onPress={() => goToSumPage()}
          >
            {intl.formatMessage({ id: 'sellSourceSbpPage.goToSumPage' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer
        onClick={() => redirectTo('../../provider-select')}
        data-testid="payment-methods.back"
      >
        <BodyText500>
          {intl.formatMessage({ id: 'sellSourceFullNamePage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  receiverBanks: state.banks,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setPhone: (phone: string) =>
      dispatch.payForm.set({
        phone,
      }),
    setReceiver: (receiver: string) =>
      dispatch.payForm.set({
        receiver,
      }),
    setReceiverBank: (bank: TBank) => {
      dispatch.payForm.set({
        receiverBank: bank,
      })
    },
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SellSourceSbpPage)
